import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { primaryRole, userRoles } from '../../utils/roles';
import api from '../../services/api';
import { useArchive } from '../../utils/archive';
import './auth.css';

// ── Forgot-password inline panel ──────────────────────────────────────────────
function ForgotPanel({ onClose }) {
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]         = useState(null); // { type: 'success'|'error', text }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setMsg({ type: 'error', text: 'Please enter your email address.' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const res = await api.post('/api/auth/forgot-password', {
        email: email.trim().toLowerCase(),
      });
      setMsg({ type: 'success', text: res.data.message });
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.error ?? 'Something went wrong. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-forgot-panel">
      <p className="auth-forgot-title">Password reset</p>
      <p className="auth-forgot-desc">
        Enter your email and we'll send you a link to reset your password.
      </p>

      {msg && (
        <div
          className={`auth-alert${msg.type === 'success' ? ' auth-alert--success' : ''}`}
          role="alert"
        >
          {msg.text}
        </div>
      )}

      {msg?.type !== 'success' ? (
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="forgot-email">
              Email address
            </label>
            <input
              id="forgot-email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="email"
            />
          </div>
          <div className="auth-forgot-actions">
            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? (
                <><span className="spinner" aria-hidden="true" /> Sending…</>
              ) : (
                'Send Reset Link'
              )}
            </button>
            <button type="button" className="auth-forgot-cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="auth-forgot-cancel" onClick={onClose}>
          Back to sign in
        </button>
      )}
    </div>
  );
}

// Where to send each role after a successful login.
const ROLE_DASHBOARD = {
  contestant: '/contestant/dashboard',
  judge: '/judge/dashboard',
  admin: '/admin/dashboard',
  observer: '/viewer/results',   // a "Viewer" account: the Results page alone
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, login } = useAuth();
  // The read-only FOOM archive: registering and password resets are refused
  // there (403), so the page must not offer them.
  const archive = useArchive();
  const [serverError, setServerError]   = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot]     = useState(false);
  const [loginAs, setLoginAs]           = useState(null); // 'contestant' | 'judge' — tracks which button is spinning
  const loginAsRef    = useRef(null);
  const navigatedRef  = useRef(false);

  // Only allow relative paths to prevent open-redirect attacks.
  const rawRedirect = searchParams.get('redirect') || '';
  const redirectTo  = rawRedirect.startsWith('/') ? rawRedirect : null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  // Already logged in when visiting /login? Redirect away.
  // Suppressed while the form is submitting — onSubmit handles routing then.
  useEffect(() => {
    if (!loading && user && !navigatedRef.current) {
      navigate(ROLE_DASHBOARD[primaryRole(user)] ?? '/login', { replace: true });
    }
  }, [user, loading, navigate]);

  const onSubmit = async (data) => {
    setServerError('');
    try {
      const res = await api.post('/api/auth/login', {
        email: data.email.trim().toLowerCase(),
        password: data.password,
      });
      login(res.data.token, res.data.user);
      const as   = loginAsRef.current;
      // Read the full role set: someone who is both a contestant and a judge has
      // users.role = 'contestant', so the old `role === 'admin'` test decided
      // nothing useful for them — the Judge button is what says where to go.
      const roles = userRoles(res.data.user);
      let dest;
      // A viewer holds only 'observer'. Neither button describes them, so
      // whichever they press they land on the results page — the only page
      // they can see. Anyone with a real role keeps the button's meaning.
      const viewerOnly = roles.includes('observer')
        && !roles.some((r) => ['admin', 'judge', 'contestant'].includes(r));
      if (viewerOnly) {
        dest = ROLE_DASHBOARD.observer;
      } else if (as === 'contestant') {
        dest = '/contestant/dashboard';
      } else {
        dest = roles.includes('admin') ? '/admin/dashboard' : '/judge/dashboard';
      }
      navigatedRef.current = true;
      navigate(dest, { replace: true });
    } catch (err) {
      setServerError(
        err.response?.data?.error ?? 'Something went wrong. Please try again.'
      );
    }
  };

  const submitAs = (as) => {
    loginAsRef.current = as;
    setLoginAs(as); // for spinner display only
    handleSubmit(onSubmit)();
  };

  // Don't render the form while AuthContext is hydrating from localStorage —
  // avoids a flash of the login page for already-authenticated users.
  if (loading) return null;

  return (
    <div className="auth-page">
      <div className="auth-card">

        {/* ── FOOM header ── */}
        <div className="auth-card__header">
          <img src="/assets/SCALA_logo.png" alt="SCALA logo" className="auth-foom__logo" />
          <div>
            <p className="auth-foom__subtitle">The SCALA Festival<br />of Original Music</p>
            <p className="auth-foom__name">FOOM</p>
            <p className="auth-foom__tagline">CELEBRATING SONGWRITING</p>
          </div>
        </div>

        <div className="auth-card__gold-divider" />

        <div className="auth-card__body">

        <h2 className="auth-form-heading">Sign In</h2>

        {/* ── Enter FOOM prompt ── */}
        {!archive && (
          <>
            <p className="auth-enter-prompt">If you don't have an account</p>
            <Link to="/register" className="auth-enter-link">Click here to ENTER FOOM</Link>
          </>
        )}
        {archive && (
          <p className="auth-enter-prompt">
            Sign in with the FOOM email and password you had in September 2026.
          </p>
        )}

        {/* Contestants kept trying their SCALA membership credentials here —
            the membership system is a separate application with separate
            accounts. */}
        {!archive && (
        <p className="auth-membership-warning">
          IMPORTANT - please note: Your SCALA membership username and password{' '}
          <strong>WILL NOT</strong> work for the FOOM competition. You need to
          create a separate FOOM account.
        </p>
        )}

        {/* ── Server error banner ── */}
        {serverError && (
          <div className="auth-alert" role="alert">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>

          {/* Email */}
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              className={`form-input${errors.email ? ' is-error' : ''}`}
              {...register('email', {
                required: 'Email is required.',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address.',
                },
              })}
            />
            {errors.email && (
              <p className="field-error" role="alert">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={`form-input${errors.password ? ' is-error' : ''}`}
                {...register('password', {
                  required: 'Password is required.',
                })}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && (
              <p className="field-error" role="alert">{errors.password.message}</p>
            )}
          </div>

          <p className="auth-login-as-label">Log in as:</p>
          <div className="auth-dual-btns">
            <button
              type="button"
              className="auth-submit-btn auth-dual-btns__primary"
              disabled={isSubmitting}
              onClick={() => submitAs('contestant')}
            >
              {isSubmitting && loginAs === 'contestant' ? (
                <><span className="spinner" aria-hidden="true" /> Signing in…</>
              ) : (
                'FOOM Entrant'
              )}
            </button>
            <button
              type="button"
              className="auth-submit-btn auth-dual-btns__outline"
              disabled={isSubmitting}
              onClick={() => submitAs('judge')}
            >
              {isSubmitting && loginAs === 'judge' ? (
                <><span className="spinner" aria-hidden="true" /> Signing in…</>
              ) : (
                'Judge / Admin'
              )}
            </button>
          </div>
        </form>

        {/* ── Forgot password ── */}
        {archive ? null : !showForgot ? (
          <p className="auth-forgot-link">
            <button
              type="button"
              className="auth-forgot-btn"
              onClick={() => setShowForgot(true)}
            >
              Forgot password?
            </button>
          </p>
        ) : (
          <ForgotPanel onClose={() => setShowForgot(false)} />
        )}

        {!archive && (
        <p className="auth-footer">
          Don't have an account?{' '}
          <Link to="/register">Register here</Link>
        </p>
        )}

        </div>{/* auth-card__body */}
      </div>
    </div>
  );
}
